import { isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, input, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { Router, RouterModule } from "@angular/router";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ButtonModule } from 'primeng/button';
import { environment } from '../../../../environments/environment';
import { IItemDetails } from '../../../models/IItemDetails';
import { ILineItems } from '../../../models/ILineItems';
import { LineItemType } from '../../../models/line-item-type';
import { IShoppingCart } from '../../../models/shopping-cart/i-shopping-cart';
import { AbsoluteMediaUriService } from '../../../services/absolute-media-uri.service';
import { ApiService } from '../../../services/api.service';
import { AuthService, LOGIN_TYPE_TICKETS } from '../../../services/authentication/auth.service';
import { EmbeddedPaymentService } from '../../../services/epf/embedded-payment.service';
import { ShoppingCartService } from '../../../services/shopping-cart/shopping-cart.service';
import { ThemeService } from '../../../services/theme.service';
import { HeaderComponent } from '../../auctions/header/header.component';
import { EmbeddedPaymentComponent, EPF_REDIRECT_PARENT_WITH_URL, ITEM_UNAVAILABLE } from '../../embedded-payment/embedded-payment.component';
import { CheckoutElementComponent } from './checkout-element/checkout-element.component';

@Component({
  selector: 'app-checkout-page',
  imports: [
    ButtonModule,
    CheckoutElementComponent,
    EmbeddedPaymentComponent,
    FontAwesomeModule,
    HeaderComponent,
    RouterModule,
  ],
  templateUrl: './checkout-page.component.html',
  styleUrl: './checkout-page.component.scss'
})
export class CheckoutPageComponent implements OnInit, OnDestroy {
  public readonly auctionId = input.required<number>();

  protected readonly absoluteMediaUriService = inject(AbsoluteMediaUriService);
  private readonly cartService = inject(ShoppingCartService);
  private readonly paymentService = inject(EmbeddedPaymentService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly themeService = inject(ThemeService);
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);

  protected cart = signal<IShoppingCart>(this.cartService.getEmptyCart());
  protected token = signal('');
  protected showEPF = signal(false);
  protected loadingDone = signal(false);
  protected auctionConfigSignal = this.apiService.auctionConfigSignal;

  protected icons = {
    faCreditCard: faCreditCard
  }

  public ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.themeService.customizeAuctionTheme(this.auctionId());
      if (!this.auctionConfigSignal()) {
        this.apiService.getAuctionConfig(this.auctionId()).subscribe();
      }
      this.cartService.cart.subscribe(cart => {
        this.cart.set(cart)
      });
      this.cart.set(this.cartService.getActiveCart(this.auctionId()));
      window.addEventListener('message', this.onHandleEpfMessage);
      this.loadingDone.set(true);
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('message', this.onHandleEpfMessage);
    }
  }

  protected items = computed(() => {
    return this.cart().lineItems.filter(x => x.type == LineItemType.Item);
  });

  protected tickets = computed(() => {
    return this.cart().lineItems.filter(x => x.type == LineItemType.Ticket);
  });

  protected donations = computed(() => {
    return this.cart().lineItems.filter(x => x.type == LineItemType.Donation);
  });

  protected currency = computed(() => {
    const config = this.apiService.auctionConfigSignal();
    if (config != null) {
      return config.currency
    }
    return '';
  })

  protected getImageUrl(item: IItemDetails | undefined | null) {
    if (item != undefined && item != null) {
      return this.absoluteMediaUriService.getAbsoluteMediaUri(item.imageUrl);
    }
    return '';
  }

  protected updateElement(item: ILineItems) {
    this.cartService.updateElementInCart(this.auctionId(), item);
  }

  protected removeElement(item: ILineItems) {
    this.cartService.removeElementFromCart(this.auctionId(), item);
  }

  protected openLoginModal() {
    const isTestAuction = this.auctionConfigSignal()?.isTestAuction;
    if (this.oidcSecurityService.authenticated().isAuthenticated) {
      this.summonEpf();
    } else {
      this.authService.openLoginModal(
        LOGIN_TYPE_TICKETS,
        this.summonEpf,
        this.auctionId(),
        this.summonEpf,
        '',
        { hideGuestOption: isTestAuction }
      );
    }
  }

  protected setEpfVisibility(visible: boolean) {
    this.showEPF.set(visible);
  }

  private summonEpf = () => {
    const auctionId = this.auctionId();
    const lineItems = this.cart().lineItems;

    if (this.auctionConfigSignal()?.isTestAuction) {
      this.paymentService.postTestPurchase(auctionId, lineItems).subscribe({
        next: () => {
          this.cartService.clearCart(auctionId);
          this.router.navigate(['/payment-successful']);
        },
        error: (error) => {
          console.error('Error processing test purchase:', error);
        }
      });
      return;
    }

    this.paymentService.postEmbeddedPayment(auctionId, lineItems).subscribe({
      next: (token) => {
        this.openEmbeddedPayment(token);
      },
      error: (error) => {
        console.error('Error getting token to open EPF.', error);
      }
    })
  };

  private openEmbeddedPayment(token: string) {
    this.token.set(token);
    this.showEPF.set(true);
  }

  private onHandleEpfMessage = (event: MessageEvent) => {
    if (event.origin !== environment.epfOrigin || event.data.type == undefined || event.data.type == null) {
      return
    };
    if (event.data.type === ITEM_UNAVAILABLE) {
      this.showEPF.set(false); // this closes modal
      // If we want to route somewhere else, example:
      // this.route.navigate(['/itemunavailable']);
      window.location.reload();
    } else if (event.data.type === EPF_REDIRECT_PARENT_WITH_URL && event.data.url) {
      console.log('Redirect to Thank You page');
      this.showEPF.set(false); // this closes modal
      this.cartService.clearCart(this.auctionId());
      window.location.replace(event.data.url);
    }
  };
}
